import { Component, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'
import { finalize } from 'rxjs/operators'

import { BaseComponent, Estimate, FeatureType, Grid, HcssActivity, HcssApiActivity } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

@Component({
    templateUrl: './heavy-bid-data.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        NgbNavOutlet,
        FormsModule,
        WjInputModule,
        WjGridModule,
        WjGridFilterModule,
        AutoSizeDirective,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        GridSelectionCalculationComponent,
    ],
})
export class HeavyBidDataComponent extends BaseComponent implements OnInit {
    active = 3
    estimateGrid: Grid<Estimate>
    hcssActivityGrid: Grid<HcssActivity>
    apiEstimateGrid: Grid<Estimate>
    apiHcssActivityGrid: Grid<HcssApiActivity>
    estimates: Estimate[] = []
    hcssActivities: HcssActivity[] = []
    apiEstimates: Estimate[] = []
    apiHcssActivities: HcssApiActivity[] = []

    private loadedTabs = new Set<number>()
    private loadingTabs = new Set<number>()

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsEdit])
    }

    constructor() {
        super()
        this.estimateGrid = new Grid<Estimate>(this.services, 'id', this).wrapColumnHeaders().isReadOnly(true)
        this.hcssActivityGrid = new Grid<HcssActivity>(this.services, 'id', this).wrapColumnHeaders().isReadOnly(true)
        this.apiEstimateGrid = new Grid<Estimate>(this.services, 'id', this)
            .wrapColumnHeaders()
            .isReadOnly(true)
            .filterBy(item => [item.code, item.name])
        this.apiHcssActivityGrid = new Grid<HcssApiActivity>(this.services, 'id', this)
            .wrapColumnHeaders()
            .isReadOnly(true)
            .filterBy(item => [
                item.estimateCode,
                item.estimateName,
                item.activityCode,
                item.activityDescription,
                item.nonAdditive,
                item.saveNonAdditive,
            ])
    }

    ngOnInit() {
        this.ensureTabLoaded(this.active)
    }

    onActiveTabChanged(id: number | string) {
        const tabId = typeof id === 'string' ? +id : id
        this.ensureTabLoaded(tabId)
    }

    private ensureTabLoaded(tabId: number) {
        if (tabId < 1 || tabId > 4 || Number.isNaN(tabId)) {
            return
        }
        if (this.loadedTabs.has(tabId) || this.loadingTabs.has(tabId)) {
            return
        }
        this.loadingTabs.add(tabId)

        const trackId = `heavyBidData-tab-${tabId}`
        const loadOne = (url: string) => this.services.data.load(true, true, this.services.data.get(url))

        switch (tabId) {
            case 1:
                this.cancelableRequest(
                    trackId,
                    false,
                    loadOne('/api/Action/GetEstimates')
                        .pipe(finalize(() => this.loadingTabs.delete(1)))
                        .subscribe({
                            next: (result: unknown[]) => {
                                this.estimates = result[0] as Estimate[]
                                this.applyEstimateComputed(this.estimates)
                                this.estimateGrid.items = this.estimates
                                this.loadedTabs.add(1)
                                if (this.isInitialLoading) {
                                    this.initialLoadingComplete()
                                }
                            },
                        }),
                )
                break
            case 2:
                this.cancelableRequest(
                    trackId,
                    false,
                    loadOne('/api/Action/GetHcssActivities')
                        .pipe(finalize(() => this.loadingTabs.delete(2)))
                        .subscribe({
                            next: (result: unknown[]) => {
                                this.hcssActivities = result[0] as HcssActivity[]
                                this.hcssActivityGrid.items = this.hcssActivities
                                this.loadedTabs.add(2)
                            },
                        }),
                )
                break
            case 3:
                this.cancelableRequest(
                    trackId,
                    false,
                    loadOne('/api/Action/GetHcssApiEstimates')
                        .pipe(finalize(() => this.loadingTabs.delete(3)))
                        .subscribe({
                            next: (result: unknown[]) => {
                                this.apiEstimates = this.mapApiEstimates((result[0] || []) as Estimate[])
                                this.applyEstimateComputed(this.apiEstimates)
                                this.apiEstimateGrid.items = this.apiEstimates
                                this.loadedTabs.add(3)
                                if (this.isInitialLoading) {
                                    this.initialLoadingComplete()
                                }
                            },
                        }),
                )
                break
            case 4:
                this.cancelableRequest(
                    trackId,
                    false,
                    loadOne('/api/Action/GetHcssApiActivities')
                        .pipe(finalize(() => this.loadingTabs.delete(4)))
                        .subscribe({
                            next: (result: unknown[]) => {
                                this.apiHcssActivities = this.mapApiActivities((result[0] || []) as HcssApiActivity[])
                                this.apiHcssActivityGrid.items = this.apiHcssActivities
                                this.loadedTabs.add(4)
                            },
                        }),
                )
                break
        }
    }

    private applyEstimateComputed(list: Estimate[]) {
        list.forEach(e => {
            ;(e as any).bondTotal = (e.addonBondTotal ?? 0) - (e.addonCost ?? 0)
            ;(e as any).overheadPercent = e.bidTotal ? (e.addonCost ?? 0) / e.bidTotal : null
        })
    }

    private mapApiEstimates(raw: Estimate[]): Estimate[] {
        return raw.map((e: Estimate) => ({
            ...e,
            dateCreated: e.dateCreated ? new Date(e.dateCreated as unknown as string) : e.dateCreated,
            bidDate: e.bidDate ? new Date(e.bidDate as unknown as string) : e.bidDate,
        }))
    }

    private mapApiActivities(raw: HcssApiActivity[]): HcssApiActivity[] {
        return raw.map((row: HcssApiActivity) => ({
            ...row,
            dateCreated: row.dateCreated ? new Date(row.dateCreated as unknown as string) : row.dateCreated,
        }))
    }
}
